import type { Campaign, ProductInput, SubmitEditRequestPayload, OfficialAudio } from '@discovr/types';
import { DELIVERABLE_REGISTRY } from '@discovr/types';

// ── Form state ────────────────────────────────────────────────────────────

export interface FormState {
  primary_goal: string;
  total_budget: number;
  description: string;
  creator_count: number;
  age_min: number;
  age_max: number;
  audience_genders: string[];
  languages: string[];
  regions: string[];
  creator_tiers: string[];
  categories: string[];
  creator_genders: string[];
  gender_notes: string;
  payout_model: string;
  content_types: string[];
  addon_types: string[];
  usage_rights_enabled: boolean;
  usage_rights_days: number;
  /** Campaign audio (Official Audio Compliance) — the resolved object, or null
   *  when the campaign has none. Edited via the resolve-then-set audio field. */
  official_audio: OfficialAudio | null;
  deliverables_text: string;
  video_title: string;
  cta: string;
  video_description: string;
  primary_focus: string;
  secondary_focus: string;
  dos: string[];
  donts: string[];
  sample_video_url: string;
  name: string;
  products: ProductInput[];
}

const USAGE_RIGHTS_ADDON_ID = 'usage_rights';

// Trims each row and drops any whose trimmed name is empty (a name-less row is "no product").
export function namedProducts(rows: ProductInput[]): ProductInput[] {
  return (rows ?? [])
    .map((r) => ({
      ...(r.product_id ? { product_id: r.product_id } : {}),
      name: (r.name ?? '').trim(),
      product_url: (r.product_url ?? '').trim(),
      notes: (r.notes ?? '').trim(),
    }))
    .filter((r) => r.name !== '');
}

function productsEqual(a: ProductInput[], b: ProductInput[]): boolean {
  const na = namedProducts(a);
  const nb = namedProducts(b);
  if (na.length !== nb.length) return false;
  return na.every((p, i) => {
    const other = nb[i];
    return (
      other !== undefined &&
      p.product_id === other.product_id &&
      p.name === other.name &&
      p.product_url === other.product_url &&
      p.notes === other.notes
    );
  });
}

// Equal when the audio ids match (ids define identity; metadata doesn't).
function officialAudioIds(a: OfficialAudio | null): string[] {
  return a?.audio_ids ?? [];
}
function officialAudioEqual(a: OfficialAudio | null, b: OfficialAudio | null): boolean {
  const ia = officialAudioIds(a);
  const ib = officialAudioIds(b);
  return ia.length === ib.length && ia.every((id, i) => id === ib[i]);
}

export function stateFromCampaign(c: Campaign, pendingOverrides?: Map<string, unknown>): FormState {
  const allTypes = c.deliverables ?? [];
  const base: FormState = {
    primary_goal: c.primary_goal,
    // Display-only default: total_budget is only submitted when touched, so
    // this 0 never turns an untouched "no budget set" campaign into a zero edit.
    total_budget: c.total_budget ?? 0,
    description: c.description,
    creator_count: c.creator_preference.creator_count,
    age_min: c.targeting.demographics.age_min,
    age_max: c.targeting.demographics.age_max,
    audience_genders: c.targeting.demographics.genders ?? [],
    languages: c.targeting.demographics.languages ?? [],
    regions: c.targeting.demographics.regions ?? [],
    creator_tiers: c.creator_preference.tiers ?? [],
    categories: c.creator_preference.categories ?? [],
    creator_genders: c.creator_preference.creator_genders ?? [],
    gender_notes: c.creator_preference.gender_notes ?? '',
    payout_model: c.payout_model,
    content_types: allTypes.filter((t) => DELIVERABLE_REGISTRY[t]?.category === 'main'),
    addon_types: allTypes.filter((t) => DELIVERABLE_REGISTRY[t]?.category === 'addon' && t !== USAGE_RIGHTS_ADDON_ID),
    usage_rights_enabled: !!c.usage_rights && c.usage_rights.days > 0,
    usage_rights_days: c.usage_rights?.days ?? 30,
    official_audio: c.official_audio ?? null,
    deliverables_text: c.deliverables_text ?? '',
    video_title: c.brief.video_title ?? '',
    cta: c.brief.cta ?? '',
    video_description: c.brief.video_description ?? '',
    primary_focus: c.brief.primary_focus ?? '',
    secondary_focus: c.brief.secondary_focus ?? '',
    dos: c.brief.dos ?? [],
    donts: c.brief.donts ?? [],
    sample_video_url: c.brief.sample_video_url ?? '',
    name: c.name ?? '',
    products: (c.products ?? []).map((p) => ({
      product_id: p.product_id,
      name: p.name,
      product_url: p.product_url ?? '',
      notes: p.notes ?? '',
    })),
  };
  if (!pendingOverrides || pendingOverrides.size === 0) return base;
  let out = base;
  for (const [path, value] of pendingOverrides) {
    out = applyPendingOverride(out, path, value);
  }
  return out;
}

// Inverse of getFieldValue: writes a pending FieldChange.new_value back into
// FormState at its dot-path. `deliverables` and `usage_rights` fan out to
// paired fields; unknown paths are ignored.
function applyPendingOverride(state: FormState, path: string, value: unknown): FormState {
  switch (path) {
    case 'name':
      return { ...state, name: (value as string | null) ?? '' };
    case 'primary_goal':
      return { ...state, primary_goal: value as string };
    case 'total_budget':
      return { ...state, total_budget: value as number };
    case 'description':
      return { ...state, description: (value as string | null) ?? '' };
    case 'deliverables_text':
      return { ...state, deliverables_text: (value as string | null) ?? '' };
    case 'deliverables': {
      const allTypes = (value as string[] | null) ?? [];
      return {
        ...state,
        content_types: allTypes.filter((t) => DELIVERABLE_REGISTRY[t]?.category === 'main'),
        addon_types: allTypes.filter((t) => DELIVERABLE_REGISTRY[t]?.category === 'addon' && t !== USAGE_RIGHTS_ADDON_ID),
      };
    }
    case 'usage_rights': {
      const ur = value as { days: number } | null;
      return {
        ...state,
        usage_rights_enabled: !!ur && ur.days > 0,
        usage_rights_days: ur?.days ?? state.usage_rights_days,
      };
    }
    case 'payout_model':
      return { ...state, payout_model: value as string };
    case 'official_audio':
      return { ...state, official_audio: (value as OfficialAudio | null) ?? null };
    case 'creator_preference.creator_count':
      return { ...state, creator_count: value as number };
    case 'creator_preference.categories':
      return { ...state, categories: (value as string[] | null) ?? [] };
    case 'creator_preference.tiers':
      return { ...state, creator_tiers: (value as string[] | null) ?? [] };
    case 'creator_preference.creator_genders':
      return { ...state, creator_genders: (value as string[] | null) ?? [] };
    case 'creator_preference.gender_notes':
      return { ...state, gender_notes: (value as string | null) ?? '' };
    case 'targeting.demographics.age_min':
      return { ...state, age_min: value as number };
    case 'targeting.demographics.age_max':
      return { ...state, age_max: value as number };
    case 'targeting.demographics.genders':
      return { ...state, audience_genders: (value as string[] | null) ?? [] };
    case 'targeting.demographics.languages':
      return { ...state, languages: (value as string[] | null) ?? [] };
    case 'targeting.demographics.regions':
      return { ...state, regions: (value as string[] | null) ?? [] };
    case 'brief.primary_focus':
      return { ...state, primary_focus: (value as string | null) ?? '' };
    case 'brief.secondary_focus':
      return { ...state, secondary_focus: (value as string | null) ?? '' };
    case 'brief.video_title':
      return { ...state, video_title: (value as string | null) ?? '' };
    case 'brief.video_description':
      return { ...state, video_description: (value as string | null) ?? '' };
    case 'brief.cta':
      return { ...state, cta: (value as string | null) ?? '' };
    case 'brief.dos':
      return { ...state, dos: (value as string[] | null) ?? [] };
    case 'brief.donts':
      return { ...state, donts: (value as string[] | null) ?? [] };
    case 'brief.sample_video_url':
      return { ...state, sample_video_url: (value as string | null) ?? '' };
    case 'products':
      return { ...state, products: (value as ProductInput[] | null) ?? [] };
    default:
      // Unrecognized field_path (a field the FE doesn't edit, or a new BE
      // field). Leave state unchanged, but warn so it doesn't fail silently.
      console.warn(`applyPendingOverride: unrecognized field_path "${path}" — pending override ignored.`);
      return state;
  }
}

export function arraysEqual(a: string[], b: string[]): boolean {
  // Exported: also used by CampaignEditForm's content-types/add-ons dirty check.
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

/** Dirty field dot-paths, matching the BE's BuildFieldChanges paths. Leaf-level
 *  only, matching the review panel's per-field granularity. */
export function computeDirtyPaths(init: FormState, cur: FormState): string[] {
  const paths: string[] = [];
  const add = (path: string, dirty: boolean) => dirty && paths.push(path);

  add('name', init.name !== cur.name);
  add('primary_goal', init.primary_goal !== cur.primary_goal);
  add('total_budget', init.total_budget !== cur.total_budget);
  add('description', init.description !== cur.description);
  add('deliverables_text', init.deliverables_text !== cur.deliverables_text);
  add('deliverables', !arraysEqual([...init.content_types, ...init.addon_types], [...cur.content_types, ...cur.addon_types]));
  add(
    'usage_rights',
    init.usage_rights_enabled !== cur.usage_rights_enabled ||
      (cur.usage_rights_enabled && init.usage_rights_days !== cur.usage_rights_days),
  );
  add('payout_model', init.payout_model !== cur.payout_model);
  add('official_audio', !officialAudioEqual(init.official_audio, cur.official_audio));

  add('creator_preference.creator_count', init.creator_count !== cur.creator_count);
  add('creator_preference.categories', !arraysEqual(init.categories, cur.categories));
  add('creator_preference.tiers', !arraysEqual(init.creator_tiers, cur.creator_tiers));
  add('creator_preference.creator_genders', !arraysEqual(init.creator_genders, cur.creator_genders));
  add('creator_preference.gender_notes', init.gender_notes !== cur.gender_notes);

  add('targeting.demographics.age_min', init.age_min !== cur.age_min);
  add('targeting.demographics.age_max', init.age_max !== cur.age_max);
  add('targeting.demographics.genders', !arraysEqual(init.audience_genders, cur.audience_genders));
  add('targeting.demographics.languages', !arraysEqual(init.languages, cur.languages));
  add('targeting.demographics.regions', !arraysEqual(init.regions, cur.regions));

  add('brief.primary_focus', init.primary_focus !== cur.primary_focus);
  add('brief.secondary_focus', init.secondary_focus !== cur.secondary_focus);
  add('brief.video_title', init.video_title !== cur.video_title);
  add('brief.video_description', init.video_description !== cur.video_description);
  add('brief.cta', init.cta !== cur.cta);
  add('brief.dos', !arraysEqual(init.dos, cur.dos));
  add('brief.donts', !arraysEqual(init.donts, cur.donts));
  add('brief.sample_video_url', init.sample_video_url !== cur.sample_video_url);
  add('products', !productsEqual(init.products, cur.products));

  return paths;
}

export function buildPayload(cur: FormState, dirty: Set<string>, comments: Record<string, string>): SubmitEditRequestPayload {
  const payload: SubmitEditRequestPayload = {};
  if (dirty.has('name')) payload.name = cur.name;
  if (dirty.has('primary_goal')) payload.primary_goal = cur.primary_goal;
  if (dirty.has('total_budget')) payload.total_budget = cur.total_budget;
  if (dirty.has('description')) payload.description = cur.description;
  if (dirty.has('deliverables_text')) payload.deliverables_text = cur.deliverables_text;
  if (dirty.has('deliverables')) payload.deliverables = [...cur.content_types, ...cur.addon_types];
  if (dirty.has('usage_rights')) {
    payload.usage_rights = cur.usage_rights_enabled ? { days: cur.usage_rights_days } : null;
  }
  if (dirty.has('payout_model')) payload.payout_model = cur.payout_model;
  // Only ever set/replace — clearing audio isn't supported (the BE setter
  // enforces >= 1 audio), so a null official_audio never ships as a change.
  if (dirty.has('official_audio') && cur.official_audio) payload.official_audio = cur.official_audio;

  const creatorPrefDirty = ['creator_preference.creator_count', 'creator_preference.categories', 'creator_preference.tiers', 'creator_preference.creator_genders', 'creator_preference.gender_notes'].some((p) => dirty.has(p));
  if (creatorPrefDirty) {
    payload.creator_preference = {
      creator_count: cur.creator_count,
      categories: cur.categories,
      tiers: cur.creator_tiers,
      creator_genders: cur.creator_genders,
      gender_notes: cur.gender_notes,
    };
  }

  const targetingDirty = ['targeting.demographics.age_min', 'targeting.demographics.age_max', 'targeting.demographics.genders', 'targeting.demographics.languages', 'targeting.demographics.regions'].some((p) => dirty.has(p));
  if (targetingDirty) {
    payload.targeting = {
      demographics: {
        age_min: cur.age_min,
        age_max: cur.age_max,
        genders: cur.audience_genders,
        languages: cur.languages,
        regions: cur.regions,
      },
    };
  }

  const briefDirty = ['brief.primary_focus', 'brief.secondary_focus', 'brief.video_title', 'brief.video_description', 'brief.cta', 'brief.dos', 'brief.donts', 'brief.sample_video_url'].some((p) => dirty.has(p));
  if (briefDirty) {
    payload.brief = {
      primary_focus: cur.primary_focus,
      secondary_focus: cur.secondary_focus,
      video_title: cur.video_title,
      video_description: cur.video_description,
      cta: cur.cta,
      dos: cur.dos,
      donts: cur.donts,
      sample_video_url: cur.sample_video_url,
    };
  }

  if (dirty.has('products')) payload.products = namedProducts(cur.products);

  // Only comments for still-dirty fields — a note left on a field the user
  // later reverted would otherwise ship keyed to a path with no FieldChange.
  const liveComments = Object.fromEntries(
    Object.entries(comments).filter(([path, text]) => dirty.has(path) && text.trim().length > 0),
  );
  if (Object.keys(liveComments).length > 0) payload.comments = liveComments;
  return payload;
}

/** Reads a dot-path's current value out of FormState (for the Comments sheet's
 *  old→new diff), using the same path set as computeDirtyPaths. */
export function getFieldValue(state: FormState, path: string): unknown {
  switch (path) {
    case 'name':
      return state.name;
    case 'primary_goal':
      return state.primary_goal;
    case 'total_budget':
      return state.total_budget;
    case 'description':
      return state.description;
    case 'deliverables_text':
      return state.deliverables_text;
    case 'deliverables':
      return [...state.content_types, ...state.addon_types];
    case 'usage_rights':
      return state.usage_rights_enabled ? { days: state.usage_rights_days } : null;
    case 'payout_model':
      return state.payout_model;
    case 'official_audio':
      return state.official_audio;
    case 'creator_preference.creator_count':
      return state.creator_count;
    case 'creator_preference.categories':
      return state.categories;
    case 'creator_preference.tiers':
      return state.creator_tiers;
    case 'creator_preference.creator_genders':
      return state.creator_genders;
    case 'creator_preference.gender_notes':
      return state.gender_notes;
    case 'targeting.demographics.age_min':
      return state.age_min;
    case 'targeting.demographics.age_max':
      return state.age_max;
    case 'targeting.demographics.genders':
      return state.audience_genders;
    case 'targeting.demographics.languages':
      return state.languages;
    case 'targeting.demographics.regions':
      return state.regions;
    case 'brief.primary_focus':
      return state.primary_focus;
    case 'brief.secondary_focus':
      return state.secondary_focus;
    case 'brief.video_title':
      return state.video_title;
    case 'brief.video_description':
      return state.video_description;
    case 'brief.cta':
      return state.cta;
    case 'brief.dos':
      return state.dos;
    case 'brief.donts':
      return state.donts;
    case 'brief.sample_video_url':
      return state.sample_video_url;
    case 'products':
      return namedProducts(state.products);
    default:
      return undefined;
  }
}

// Numeric inputs report NaN while empty (so the box can show blank, not a stuck
// 0). At save time an emptied field here reverts to the campaign's existing
// value instead of submitting NaN. usage_rights_days is deliberately excluded:
// its own validity gate handles empty/invalid, so it must not silently revert.
export const NUMERIC_FALLBACK_KEYS = [
  'total_budget',
  'creator_count',
  'age_min',
  'age_max',
] as const satisfies ReadonlyArray<keyof FormState>;

// Replaces any non-finite numeric field (NaN from an emptied input) with the
// initial value. A finite 0 is a real value and is preserved — only NaN reverts.
export function withNumericFallback(form: FormState, initial: FormState): FormState {
  let out = form;
  for (const key of NUMERIC_FALLBACK_KEYS) {
    if (!Number.isFinite(out[key])) {
      out = { ...out, [key]: initial[key] };
    }
  }
  return out;
}
